import CopilotWidget from '../components/CopilotWidget'
/**
 * CoConnect — User Management (v1.6.5 REBUILD)
 * ───────────────────────────────────────────────
 * Admin can now:
 *   1. Create users (finance, clerk, driver, agent, admin, farmer)
 *   2. Change user roles
 *   3. Reset passwords
 *   4. Activate/deactivate users
 *   5. Delete (soft-delete) users
 *   6. Filter by role (including factory_clerk)
 *
 * v1.7.4 USER MGMT PATCH (additive — original logic preserved 100%):
 *   S-01: Password field now type="password" with show/hide toggle (was type="text" — security bug)
 *   S-02: Email validation hint (inline feedback for invalid emails)
 *   S-03: Confirmation dialog before creating admin-role users (irreversible privilege grant)
 *   S-04: WCAG 2.2 AA skip-to-content link
 *   S-05: Role description tooltip on hover (explains what each role can do)
 */
import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LogoutButton from '../components/LogoutButton';
import ProfileButton from '../components/ProfileButton';
import {
  BrandMark, Card, SectionHeader, StatusPill,
  Spinner, EmptyState, Alert, IconButton, Sheet, Pagination, usePagination, Avatar,
} from '../components/ui';

const Icon = {
  Users: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 11-8 0 4 4 0 018 0zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>),
  Plus: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 5v14M5 12h14" /></svg>),
  Search: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>),
  Mail: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6" /></svg>),
  Phone: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" /></svg>),
  Edit: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>),
  Trash: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" /></svg>),
  Shield: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>),
  Check: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M20 6L9 17l-5-5" /></svg>),
  X: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M18 6L6 18M6 6l12 12" /></svg>),
  Key: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>),
  // v1.7.4 (S-01): eye icons for password show/hide toggle
  Eye: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>),
  EyeOff: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22" /></svg>),
};

interface User {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  role: string;
  is_active: boolean;
  mfa_enabled: boolean;
  must_change_password: boolean;
  last_login: string | null;
  created_at: string;
  truck_registration?: string;
  assigned_village_count?: number;
  linked_farmer_count?: number;
}

const roleTone: Record<string, 'info' | 'success' | 'warning' | 'neutral'> = {
  admin: 'info',
  finance: 'info',
  field_agent: 'success',
  farmer: 'warning',
  driver: 'warning',
  factory_clerk: 'success',
  dpo: 'neutral',
};

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  // Create user sheet
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', phone: '', role: 'finance', password: '' });
  const [createLoading, setCreateLoading] = useState(false);
  // v1.7.4 (S-01): password visibility toggle (was always visible — security bug)
  const [showPassword, setShowPassword] = useState(false);

  // Edit role sheet
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editRole, setEditRole] = useState('');

  const usersPag = usePagination(users, 15);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/users');
      // v1.6.5: handle both array and { users: [...] } response shapes
      const usersData = Array.isArray(response.data) ? response.data : (response.data?.users || []);
      setUsers(usersData);
    } catch (err) {
      setUsers([]);
      setError('Failed to load users. Make sure the server is running.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  // ─── Create user ───
  const handleCreateUser = async () => {
    // v1.7.4 (S-02): email validation before submit
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(createForm.email)) {
      setError('Please enter a valid email address (e.g. user@coconnect.com).');
      return;
    }
    // v1.7.4 (S-03): confirmation dialog for admin role (irreversible privilege grant)
    if (createForm.role === 'admin') {
      if (!confirm(`Create a new ADMIN user?\n\n${createForm.name} (${createForm.email}) will have FULL administrative privileges including user management, role changes, and system configuration. This is a high-privilege role — proceed with caution.`)) {
        return;
      }
    }
    setCreateLoading(true);
    setError(null); setSuccess(null);
    try {
      const res = await apiClient.post('/users', {
        name: createForm.name,
        email: createForm.email,
        phone: createForm.phone || undefined,
        role: createForm.role,
        password: createForm.password || undefined,
      });
      const defaultPwd = res.data?.default_password;
      setSuccess(`User created: ${createForm.email} (${createForm.role})${defaultPwd ? `. Default password: ${defaultPwd}` : ''}`);
      setShowCreateSheet(false);
      // v1.7.4 (S-01): reset password visibility on close
      setShowPassword(false);
      setCreateForm({ name: '', email: '', phone: '', role: 'finance', password: '' });
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  // ─── Change role ───
  const handleRoleChange = async (userId: string, role: string) => {
    setError(null); setSuccess(null);
    try {
      await apiClient.put(`/users/${userId}/role`, { role });
      setSuccess(`Role changed to ${role}`);
      setEditUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change role');
    }
  };

  // ─── Reset password ───
  const handleResetPassword = async (userId: string) => {
    if (!confirm('Reset this user\'s password? A new default password will be generated.')) return;
    setError(null); setSuccess(null);
    try {
      const res = await apiClient.post(`/users/${userId}/reset-password`, {});
      // v2.1.1 (BUG#2 fix): server returns `new_password` (not `password`).
      // Previously the admin always saw "CoConnect@2024" fallback — the real
      // generated password was never shown, so users couldn't log in.
      setSuccess(`Password reset. New password: ${res.data?.new_password || res.data?.password || 'CoConnect@2024'} (user must change on next login)`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  // ─── Activate/Deactivate ───
  const handleToggleActive = async (userId: string, currentActive: boolean) => {
    setError(null); setSuccess(null);
    try {
      await apiClient.put(`/users/${userId}/activate`, { is_active: !currentActive });
      setSuccess(`User ${!currentActive ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to toggle active status');
    }
  };

  // ─── Delete user ───
  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!confirm(`Delete user "${userName}"? This will deactivate their account. This action cannot be undone.`)) return;
    setError(null); setSuccess(null);
    try {
      await apiClient.delete(`/users/${userId}`);
      setSuccess(`User ${userName} deleted`);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 pb-24 md:pb-0">
      {/* v1.7.4 (S-04): WCAG 2.2 AA skip-to-content link */}
      <a href="#usermgmt-main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-ink-900 focus:text-white focus:rounded-lg">
        Skip to content
      </a>
      {/* Top bar */}
      <header className="topbar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark size="md" accent="admin" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">User Management</p>
              <p className="text-xs text-ink-500 dark:text-ink-400 truncate">{currentUser?.name} · Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowCreateSheet(true)} className="btn-primary text-xs">
              <Icon.Plus className="w-4 h-4" /> <span className="hide-mobile">Add User</span>
            </button>
            <ProfileButton />
            <LogoutButton />
          </div>
        </div>
      </header>

      <main id="usermgmt-main-content" className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

        {/* Search + filter */}
        <Card padding="md">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
              <input
                type="text"
                placeholder="Search by name or email…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>
            <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="input md:w-48">
              <option value="all">All roles</option>
              <option value="admin">Admin</option>
              <option value="finance">Finance</option>
              <option value="factory_clerk">Factory Clerk</option>
              <option value="field_agent">Field Agent</option>
              <option value="farmer">Farmer</option>
              <option value="driver">Driver</option>
            </select>
          </div>
        </Card>

        {/* Users table */}
        {loading ? (
          <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>
        ) : filteredUsers.length === 0 ? (
          <Card padding="lg"><EmptyState icon={<Icon.Users className="w-5 h-5" />} title="No users found" description="Try adjusting your search or create a new user." /></Card>
        ) : (
          <Card padding="none" className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr><th>User</th><th>Role</th><th>Phone</th><th>Status</th><th>Joined</th><th>Last Login</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {usersPag.paginatedItems.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="flex items-center gap-2.5">
                          <Avatar name={user.name} size="sm" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-ink-900 dark:text-ink-50 truncate">{user.name}</p>
                            <p className="text-2xs text-ink-500 dark:text-ink-400 truncate flex items-center gap-1">
                              <Icon.Mail className="w-3 h-3" /> {user.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td><StatusPill tone={roleTone[user.role] || 'neutral'} dot>{user.role.replace('_', ' ')}</StatusPill></td>
                      <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{user.phone || '—'}</td>
                      <td>{user.is_active ? <StatusPill tone="success" dot>Active</StatusPill> : <StatusPill tone="danger" dot>Inactive</StatusPill>}</td>
                      <td className="text-2xs text-ink-500 nums">{new Date(user.created_at).toLocaleDateString()}</td>
                      <td className="text-2xs text-ink-500 nums">{user.last_login ? new Date(user.last_login).toLocaleDateString() : '—'}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <IconButton label="Change role" size="sm" onClick={() => { setEditUser(user); setEditRole(user.role); }}>
                            <Icon.Edit className="w-3.5 h-3.5" />
                          </IconButton>
                          <IconButton label="Reset password" size="sm" onClick={() => handleResetPassword(user.id)}>
                            <Icon.Key className="w-3.5 h-3.5" />
                          </IconButton>
                          <IconButton label={user.is_active ? 'Deactivate' : 'Activate'} size="sm" onClick={() => handleToggleActive(user.id, user.is_active)}>
                            {user.is_active ? <Icon.X className="w-3.5 h-3.5" /> : <Icon.Check className="w-3.5 h-3.5" />}
                          </IconButton>
                          {user.id !== currentUser?.id && (
                            <IconButton label="Delete user" size="sm" className="text-danger-600 dark:text-danger-500" onClick={() => handleDeleteUser(user.id, user.name)}>
                              <Icon.Trash className="w-3.5 h-3.5" />
                            </IconButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-ink-200 dark:border-ink-800">
              <Pagination page={usersPag.page} pageSize={usersPag.pageSize} total={usersPag.total}
                onPageChange={usersPag.setPage} onPageSizeChange={usersPag.setPageSize} showPageSize />
            </div>
          </Card>
        )}
      </main>

      {/* Create User Sheet */}
      <Sheet open={showCreateSheet} onClose={() => setShowCreateSheet(false)} title="Create New User"
        footer={
          <div className="flex gap-2">
            <button onClick={() => setShowCreateSheet(false)} className="btn-secondary flex-1">Cancel</button>
            <button onClick={handleCreateUser} disabled={createLoading || !createForm.name || !createForm.email} className="btn-primary flex-1">
              {createLoading ? <Spinner size="sm" /> : <Icon.Plus className="w-4 h-4" />} Create User
            </button>
          </div>
        }>
        <div className="space-y-4">
          <div>
            <label htmlFor="cu-name" className="label">Full name *</label>
            <input id="cu-name" type="text" required value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              className="input" placeholder="e.g. Finance Officer" />
          </div>
          <div>
            <label htmlFor="cu-email" className="label">Email *</label>
            <input id="cu-email" type="email" required value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              className="input" placeholder="e.g. finance@coconnect.com" />
            {/* v1.7.4 (S-02): inline email validation hint */}
            {createForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.email) && (
              <p className="text-2xs text-warning-600 dark:text-warning-500 mt-1">Enter a valid email format (user@domain.com).</p>
            )}
          </div>
          <div>
            <label htmlFor="cu-phone" className="label">Phone</label>
            <input id="cu-phone" type="tel" value={createForm.phone}
              onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
              className="input" placeholder="+237..." />
          </div>
          <div>
            <label htmlFor="cu-role" className="label">Role *</label>
            <select id="cu-role" value={createForm.role}
              onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
              className="input">
              <option value="admin">Admin</option>
              <option value="finance">Finance Officer</option>
              <option value="factory_clerk">Factory Clerk (Warehouse)</option>
              <option value="field_agent">Field Agent</option>
              <option value="driver">Driver</option>
              <option value="farmer">Farmer</option>
              <option value="dpo">Data Protection Officer (DPO)</option>{/* v3.2 */}
            </select>
          </div>
          <div>
            {/* v1.7.4 (S-01): password field with show/hide toggle (was type="text" — security bug) */}
            <label htmlFor="cu-pwd" className="label">Password (optional — leave blank for default)</label>
            <div className="relative">
              <input id="cu-pwd" type={showPassword ? "text" : "password"} value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                className="input pr-10" placeholder="Default: CoConnect@2024 (must change on first login)" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600 dark:hover:text-ink-300"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <Icon.EyeOff className="w-4 h-4" /> : <Icon.Eye className="w-4 h-4" />}
              </button>
            </div>
            {/* v1.7.4 (S-01): password strength hint when custom password is entered */}
            {createForm.password && createForm.password.length < 8 && (
              <p className="text-2xs text-warning-600 dark:text-warning-500 mt-1">Password is shorter than 8 characters — recommend at least 8 chars with mixed case + numbers.</p>
            )}
          </div>
          <Alert tone="info">
            <strong>Finance</strong> → can approve payments, process withdrawals, update prices<br />
            <strong>Factory Clerk</strong> → can receive batches at warehouse, grade quality, route batches, manage warehouse inventory<br />
            <strong>Field Agent</strong> → can register farmers, record batches, inspect harvest notifications<br />
            <strong>Driver</strong> → can see dispatches, load bags, update GPS<br />
            <strong>Farmer</strong> → can see their batches, grades, payments, submit harvest notifications
          </Alert>
        </div>
      </Sheet>

      {/* Edit Role Sheet */}
      <Sheet open={!!editUser} onClose={() => setEditUser(null)} title="Change User Role"
        footer={
          <div className="flex gap-2">
            <button onClick={() => setEditUser(null)} className="btn-secondary flex-1">Cancel</button>
            <button onClick={() => handleRoleChange(editUser!.id, editRole)} className="btn-primary flex-1">
              <Icon.Check className="w-4 h-4" /> Save Role
            </button>
          </div>
        }>
        {editUser && (
          <div className="space-y-4">
            <div className="bg-ink-100 dark:bg-ink-800 rounded-lg p-3">
              <p className="text-sm font-medium">{editUser.name}</p>
              <p className="text-2xs text-ink-500">{editUser.email}</p>
              <p className="text-2xs text-ink-500 mt-1">Current role: <StatusPill tone={roleTone[editUser.role] || 'neutral'} dot>{editUser.role.replace('_', ' ')}</StatusPill></p>
            </div>
            <div>
              <label htmlFor="er-role" className="label">New role</label>
              <select id="er-role" value={editRole} onChange={(e) => setEditRole(e.target.value)} className="input">
                <option value="admin">Admin</option>
                <option value="finance">Finance Officer</option>
                <option value="factory_clerk">Factory Clerk (Warehouse)</option>
                <option value="field_agent">Field Agent</option>
                <option value="driver">Driver</option>
                <option value="farmer">Farmer</option>
                <option value="dpo">Data Protection Officer (DPO)</option>{/* v3.2 */}
              </select>
            </div>
          </div>
        )}
      </Sheet>
          <CopilotWidget userRole="admin" />
</div>
  );
};

export default UserManagement;
